import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { VIAGENS_CONTROLE_ROUTES } from 'lib/routes';
import {
  canCreateBilling,
  canListBillings,
  canValidateViagem,
  VIAGENS_CONTROLE_PERMISSIONS as P,
} from 'lib/viagens-controle-permissions';
import { usePermission } from 'hooks/usePermission';
import { cn } from 'lib/utils';

import * as S from './styles';

type TabRoute = {
  title: string;
  path: string;
  isAllowed: boolean;
  isNew?: boolean;
};

export const TabbedNavigation = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const location = useLocation();
  const { hasAnyPermissionTo } = usePermission();

  const routes = React.useMemo((): TabRoute[] => {
    return [
      {
        title: 'Acompanhamento de estadia',
        path: VIAGENS_CONTROLE_ROUTES.monitoring().url,
        isAllowed: true,
        isNew: true,
      },
      {
        title: 'Gestão à vista',
        path: `${VIAGENS_CONTROLE_ROUTES.monitoring().url}?view=indicators`,
        isAllowed: hasAnyPermissionTo([P.MANAGEMENT]),
        isNew: true,
      },
      {
        title: 'Estadias',
        path: `${VIAGENS_CONTROLE_ROUTES.monitoring().url}?view=settlement`,
        isAllowed: hasAnyPermissionTo([P.STAYS]),
        isNew: true,
      },
      {
        title: 'Faturas',
        path: `${VIAGENS_CONTROLE_ROUTES.monitoring().url}?view=invoices`,
        isAllowed: hasAnyPermissionTo([P.INVOICES]),
        isNew: true,
      },
      {
        title: 'Relatórios',
        path: `${VIAGENS_CONTROLE_ROUTES.monitoring().url}?view=reports`,
        isAllowed: hasAnyPermissionTo([P.MANAGEMENT]),
        isNew: true,
      },
      {
        title: 'Viagens',
        path: VIAGENS_CONTROLE_ROUTES.list().url,
        isAllowed: true,
      },
      {
        title: 'Validação Operacional',
        path: VIAGENS_CONTROLE_ROUTES.approvals().url,
        isAllowed: canValidateViagem(hasAnyPermissionTo),
      },
      {
        title: 'Gerar Fatura',
        path: VIAGENS_CONTROLE_ROUTES.createBilling().url,
        isAllowed: canCreateBilling(hasAnyPermissionTo),
      },
      {
        title: 'Aprovar faturas',
        path: VIAGENS_CONTROLE_ROUTES.billings().url,
        isAllowed: canListBillings(hasAnyPermissionTo),
      },
    ].filter((route) => route.isAllowed);
  }, [hasAnyPermissionTo]);

  const isActive = React.useCallback(
    (path: string) => {
      const [pathname, search] = path.split('?');
      if (pathname === VIAGENS_CONTROLE_ROUTES.monitoring().url) {
        const current =
          new URLSearchParams(location.search).get('view') || 'timeline';
        const target = new URLSearchParams(search).get('view') || 'timeline';
        return location.pathname === pathname && current === target;
      }
      if (
        path === VIAGENS_CONTROLE_ROUTES.list().url ||
        path === VIAGENS_CONTROLE_ROUTES.billings().url
      ) {
        return location.pathname === path;
      }

      return location.pathname.startsWith(path);
    },
    [location.pathname, location.search]
  );

  return (
    <div {...props} className={cn('flex flex-col w-full mb-6', className)}>
      <S.Container aria-label="Navegação de estadias">
        {routes.map((route) => (
          <S.Button
            key={route.path}
            to={route.path}
            className={isActive(route.path) ? 'active' : ''}
            aria-current={isActive(route.path) ? 'page' : undefined}
          >
            {route.isNew && (
              <span aria-hidden="true" className="new-feature-star">
                ★
              </span>
            )}
            {route.title}
          </S.Button>
        ))}
      </S.Container>
    </div>
  );
};
